import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clientConfig } from '../../../config';
import * as translator from '../../../utils/translator';
import * as http from '../../../utils/http';
import logo_v2025 from '../../../images/logo_v2025.png';
import RepaySuccessState from './RepaySuccessState';
import RepayInvalidLinkState from './RepayInvalidLinkState';
import RepayCheckoutState from './RepayCheckoutState';
import type { RepayPspInitData } from './RepayCheckoutState';

type CheckoutData =
  | { status: 'invalid' }
  | { status: 'expired' }
  | { status: 'paid'; partNumber: string; paymentDate: string; amountPaid: string; maskedCardNumber: string }
  | {
      status: 'pending';
      amount: number;
      currency: string;
      components: Array<{ componentType: string; componentName: string | null; quantity: number; amount: number }>;
      paymentServiceProvider: string;
      pspInitData: RepayPspInitData;
      customerData: { first_name: string; last_name: string; email: string };
      orderAddresses: { country_id: string; city: string; street: string; zip: string; house_number: string; phone: string } | null;
    };

type HttpBlobResponse = {
  data: BlobPart;
  headers?: Record<string, string>;
};

const saveBlob = (response: HttpBlobResponse, fileName: string) => {
  const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const RepayPage: React.FC = () => {
  const { country, language, token } = useParams<{ country: string; language: string; token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState<CheckoutData | null>(null);
  const [isDownloadingConfirmation, setIsDownloadingConfirmation] = useState(false);

  const homeUrlPrefix = translator.getCMSUrlPrefixByLocale(translator.getLocaleByCountry(country));
  const goHome = () => (window.location.href = clientConfig.MAIN_WEBSITE_URL + '/' + homeUrlPrefix);

  const fetchCheckoutData = () => {
    http
      .getFromServer(`payment-link/${token}?language=${language}`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((response: { data: CheckoutData }) => setData(response.data))
      .catch((error: any) => setData(error.response?.data ?? { status: 'invalid' }));
  };

  const downloadConfirmation = () => {
    if (!token || isDownloadingConfirmation) {
      return;
    }
    const paidData = data?.status === 'paid' ? data : null;
    const invoiceDate = paidData?.paymentDate ? new Date(paidData.paymentDate) : new Date();
    const datePart = Number.isNaN(invoiceDate.getTime())
      ? new Date().toISOString().slice(0, 10).replace(/-/g, '')
      : `${invoiceDate.getFullYear()}${String(invoiceDate.getMonth() + 1).padStart(2, '0')}${String(invoiceDate.getDate()).padStart(2, '0')}`;
    const fileName = `Repayment_Confirmation_${paidData?.partNumber ?? token}_${datePart}.pdf`;

    setIsDownloadingConfirmation(true);
    http
      .getFromServer(`payment-link/${token}/confirmation`, [http.MIDDLEWARES.GENERATE_FILE, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response: HttpBlobResponse) => saveBlob(response, fileName))
      .catch((error: any) => {
        window.clientLogger?.error?.('Failed to download repayment confirmation', error);
      })
      .finally(() => setIsDownloadingConfirmation(false));
  };

  useEffect(() => {
    if (searchParams.get('preview') === 'invalid') {
      setData({ status: 'invalid' });
      return;
    }

    fetchCheckoutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchParams]);

  return (
    <>
      <header className="flex items-center justify-center px-[20px] py-[25px] bg-white">
        <img src={logo_v2025} alt="DoktorABC" className="h-[28px] w-[171px] cursor-pointer" onClick={goHome} />
      </header>

      <div className="bg-[#f7f7f7] min-h-screen">
        {!data ? null : data.status === 'paid' ? (
          <RepaySuccessState
            language={language!}
            partNumber={data.partNumber}
            paymentDate={data.paymentDate}
            amountPaid={data.amountPaid}
            maskedCardNumber={data.maskedCardNumber}
            onDownloadConfirmation={downloadConfirmation}
            onBackToHomepage={goHome}
          />
        ) : data.status === 'pending' ? (
          <RepayCheckoutState country={country!} language={language!} token={token!} checkoutData={data} onPaid={fetchCheckoutData} />
        ) : (
          <RepayInvalidLinkState
            language={language!}
            onMyOrders={() => navigate(`/${country}/account/my-orders`)}
            onContactUs={() => navigate(`/${country}/contact`)}
          />
        )}
      </div>
    </>
  );
};

export default RepayPage;
